import { Collapsible } from '@ui/animated/Collapsible'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { colors } from '@ui/colors'
import DetailRow from '@ui/elements/DetailRow'
import { IconName } from '@ui/elements/Icon'
import PriceInfo from '@/price/PriceInfo'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import { isNumber } from 'lodash'
import { useState } from 'react'
import { Platform, View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import { Image } from 'expo-image'
import { lineBreakToSpace } from 'utils/string'

interface SummaryGroupItem {
  icon: IconName
  title: string
  desc?: string
  price?: number
  disabled?: boolean
}

interface Props {
  icon?: IconName
  title: string
  desc?: string
  total: number | React.ReactNode
  priceSuffix?: string
  items?: SummaryGroupItem[]
  onPress?: () => void
  className?: string
  color?: string
  isModal?: boolean
  borderBottom?: boolean
  expanded?: boolean
  image?: string
}

export default function SummaryGroup({
  icon,
  title,
  desc,
  total,
  items,
  className,
  color,
  isModal,
  borderBottom,
  priceSuffix,
  expanded,
  image,
  ...props
}: Props) {
  const [open, setOpen] = useState(!!expanded)

  const styleHideOnOpen = useAnimatedOpacity(!open)
  const isOpenAnimation = useAnimatedToggle(open)
  const isEmpty = Boolean(!items || items.length === 0)

  const styleBorder = useAnimatedStyle(() => {
    return {
      borderColor: interpolateColor(
        isOpenAnimation.value,
        [0, 1],
        [colors.text10, colors.text10]
      ),
    }
  })

  const styleContainer = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        isOpenAnimation.value,
        [0, 1],
        // [colors.white, colors.gray[50]]
        [colors.white, colors.white]
      ),
    }
  })

  const styleImageContainer = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: interpolate(
            isOpenAnimation.value,
            [0, 1],
            [0, imageWidth]
          ),
        },
      ],
    }
  })

  const onPress = () => (props.onPress ? props.onPress() : setOpen(!open))

  const groupIcon: IconName = isEmpty
    ? 'point-filled'
    : icon || (open ? 'chevron-up' : 'chevron-down')

  const offsetChildren = isModal ? 80 : 48

  return (
    <Animated.View
      className={cn(
        'flex flex-col gap-0 overflow-hidden',
        borderBottom !== false && 'border-b',
        className
      )}
      style={[styleContainer, styleBorder]}
    >
      <DetailRow
        title={title}
        desc={desc}
        icon={groupIcon}
        onPress={onPress}
        className={cn('rounded-none border-0', isModal && 'px-16')}
        color={color}
        classNameDesc="uppercase"
        stronger={open}
      >
        {!isEmpty && (
          <View className="flex flex-row items-center gap-8">
            {image && (
              <Animated.View className="" style={styleImageContainer}>
                <Image
                  source={{ uri: image }}
                  style={[{ width: imageWidth, height: imageHeight }]}
                  contentFit="contain"
                />
              </Animated.View>
            )}
            <Animated.View style={[styleHideOnOpen, { minWidth: 96 }]}>
              {isNumber(total) ? (
                <PriceInfo value={total} size="default" suffix={priceSuffix} />
              ) : (
                total
              )}
            </Animated.View>
          </View>
        )}
      </DetailRow>
      <Collapsible fullWidth open={open}>
        {items?.map(({ icon, title, desc, price, disabled }, index) => (
          <View key={title}>
            <DetailRow
              first={index !== 0}
              className={cn('rounded-none', isModal ? 'pl-32 pr-16' : 'pl-24')}
              icon={icon}
              title={title}
              desc={lineBreakToSpace(desc)}
              classNameInner={cn(disabled && 'opacity-25')}
            >
              {!disabled && (
                <PriceInfo value={price} size="default" suffix={priceSuffix} />
              )}
            </DetailRow>
            {index < items.length - 1 && (
              <View
                className="relative left-0 top-0 bg-black/10"
                style={{
                  height: 1,
                  transform: [{ translateX: offsetChildren }],
                }}
              />
            )}
          </View>
        ))}
        <View
          className="bg-text absolute left-0 top-0 h-full"
          style={{ width: 2, transform: [{ translateX: offsetChildren - 1 }] }}
        />
      </Collapsible>
    </Animated.View>
  )
}

const imageWidth = 128
const imageHeight = 64
